import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import logger from '../lib/logger';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend, LabelList
} from 'recharts';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Loader2, Target, Users, Zap, DollarSign, AlertTriangle, ArrowRight,
  Heart, Upload, Download, GitCompare, Clock, User, Info, ChevronDown, ChevronUp, BookOpen,
  Eye, ShieldCheck, TrendingUp, TrendingDown, ArrowUpDown, ClipboardCheck
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useDeepLinkOrg } from '../hooks/useDeepLinkOrg';
import { TileInfoButton } from '../components/common/TileInfoButton';
import { useQuarterDelta } from '../hooks/useQuarterDelta';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { HeroAccents } from '../components/layout/HeroAccents';
import DataAsOf from '../components/shared/DataAsOf';
import ChainPositionStrip from '../components/layout/ChainPositionStrip';
import { getLightSectionTheme, getSectionTheme } from '../lib/sectionThemes';
import { useTheme } from '../context/ThemeContext';
import PageSubNav from '../components/layout/PageSubNav';
import RoeLeanOverview, { CohortTransferTable, DataGapsStrip } from '../components/roe/RoeLeanOverview';
import GapCoachingCard from '../components/roe/GapCoachingCard';
import EvidenceImportsTable from '../components/roe/EvidenceImportsTable';
import InteractiveTrendChart from '../components/common/InteractiveTrendChart';
import TrendInsightNarrator from '../components/common/TrendInsightNarrator';

const ROE_SUBNAV = [
  { id: 'overview', label: 'Overview', to: '/roe-health', end: true },
  { id: 'priorities', label: 'Priorities', to: '/roe-health/priorities' },
  { id: 'drivers', label: 'Drivers & Constraints', to: '/roe-health/drivers' },
  { id: 'capabilities', label: 'Capabilities', to: '/roe-health/capabilities' },
  { id: 'cohorts', label: 'Cohorts', to: '/roe-health/cohorts' },
  { id: 'influence', label: 'Influence', to: '/roe-health/influence' },
  { id: 'evidence', label: 'Evidence', to: '/roe-health/evidence' },
  { id: 'history', label: 'History', to: '/roe-health/history' },
];
const PENDING_SUBVIEWS = {};
import TransferEvidence from '../components/roe/TransferEvidence';
import TransferHistory from '../components/roe/TransferHistory';
import TransferOverview from '../components/roe/TransferOverview';
import TransferDrivers from '../components/roe/TransferDrivers';
import TransferCapabilities from '../components/roe/TransferCapabilities';
import TransferCohorts from '../components/roe/TransferCohorts';
import TransferInfluence from '../components/roe/TransferInfluence';
import EngineWeakLinkCallout from '../components/engines/EngineWeakLinkCallout';
import EngineActionPlanCard from '../components/engines/EngineActionPlanCard';
import CollapsibleSection from '../components/layout/CollapsibleSection';
import PageToolbar from '../components/layout/PageToolbar';
import FilterSummaryChip from '../components/layout/FilterSummaryChip';

// Ordinal suffix for percentile display (1st, 2nd, 3rd, 81st…).
const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};


// Tab content info — same pattern as MaturityHealthPage. Keep each
// `body` to ~3 sentences; popover is 320px wide.
const TAB_INFO = {
  health: {
    title: 'Actuals tab',
    body: 'What is *actually happening* in your transfer pipeline right now: real L2 assessment scores, motivation surveys, and L3 manager observations rolled up into Actual ROE, Capability, Motivation, L3 Behavior, and PE Validated. What to do: red/amber values mean transfer is leaking — focus the Deficiency Analysis section to see exactly where to intervene next.',
  },
  variance: {
    title: 'Variance tab',
    body: 'Modelled (forecast) vs Actual ROE for every metric. Negative variance means real-world transfer is underperforming the assumptions baked into your ROE Modeller scenario. What to do: large negative variances signal that your model assumptions need recalibrating, or your transfer system needs urgent attention — click "Model ROE" to update the projections.',
  },
};


function getColor(pct) {
  if (pct >= 80) return '#06b6d4';
  if (pct >= 55) return '#14b8a6';
  if (pct >= 30) return '#f59e0b';
  return '#ef4444';
}

const severityStyles = {
  Critical: { card: 'border-rose-300 bg-rose-50', text: 'text-rose-800', badge: 'bg-rose-100 text-rose-700 border-rose-200' },
  High: { card: 'border-amber-300 bg-amber-50', text: 'text-amber-800', badge: 'bg-amber-100 text-amber-700 border-amber-200' },
  Moderate: { card: 'border-blue-200 bg-blue-50', text: 'text-blue-800', badge: 'bg-blue-100 text-blue-700 border-blue-200' },
};

// ──── Info content for each actuals card ────
const ACTUALS_INFO = {
  roe: {
    what: 'Return on Expectation — the composite score measuring whether your learning investment actually translated into on-the-job behavior change.',
    how: 'ROE = (0.4 × Learner Performance Shift) + (0.6 × L3 Stakeholder Impact). LPS captures the L2 learning shift; SI captures the L3 behavior evidence.',
    why: 'ROE is the reality multiplier. It validates whether the workforce capacity predicted by the People Engine actually materialized in the workplace. Without ROE, all projections remain theoretical.',
  },
  capability: {
    what: 'The weighted average of L2 assessment data: skills uplift from pre/post tests, certification pass rates, and 30-day knowledge retention.',
    how: 'C = (0.4 × Skills Uplift) + (0.3 × Certification Rate) + (0.3 × Knowledge Retention). Measured from actual assessment records.',
    why: 'Capability is the "C" in the PE equation (C × M). It measures whether learners acquired the knowledge and skills. High capability with low L3 means people learned but aren\'t applying it.',
  },
  motivation: {
    what: 'The weighted average of motivation survey data: intrinsic drive (purpose, mastery), extrinsic factors (recognition, career), confidence to apply, and manager support.',
    how: 'M = (0.3 × Intrinsic) + (0.3 × Extrinsic) + (0.2 × Confidence) + (0.2 × Manager Support). From actual survey responses.',
    why: 'Motivation is the "M" multiplier in PE. Even high capability is wasted without motivation — people won\'t change behavior. Manager support is the single strongest predictor of training transfer.',
  },
  l3: {
    what: 'Manager-observed behavior change on the job — the L3 (Kirkpatrick Level 3) composite score from structured observations.',
    how: 'L3 = (0.35 × Behavior Change) + (0.30 × Skill Application) + (0.20 × Performance Improvement) + (0.15 × Manager Confidence). From actual manager observation records.',
    why: 'L3 carries 60% weight in the ROE calculation because on-the-job behavior change is the most reliable predictor of business impact. Low L3 is the #1 reason learning investments fail to deliver ROI.',
  },
  pe_validated: {
    what: 'The proven workforce capacity after applying the ROE reality multiplier. PE Validated = PE Ideal × ROE.',
    how: 'PE Ideal (C × M) represents theoretical capacity. ROE adjusts it based on actual behavior evidence. The gap between Ideal and Validated is "capability leakage" — learning that didn\'t transfer.',
    why: 'PE Validated is the number that drives KPI projections and financial ROI. It represents what your learning investment actually delivered — not what it could have delivered in theory.',
  },
};

// ──── Actuals card with info toggle ────
function ActualCardWithInfo({ label, value, subtext, color, bgStyle, infoKey, className }) {
  const [showInfo, setShowInfo] = useState(false);
  const info = ACTUALS_INFO[infoKey];
  return (
    <Card style={bgStyle} className={cn(className)} data-testid={`actual-card-${infoKey}`}>
      <CardContent className="pt-4 pb-3">
        <div className="text-center" style={bgStyle ? { color: 'white' } : {}}>
          <div className="flex items-center justify-center gap-1">
            <p className={cn("text-[10px] uppercase tracking-wider font-semibold", bgStyle ? 'opacity-70' : 'text-slate-500')}>{label}</p>
            {info && (
              <button onClick={() => setShowInfo(!showInfo)} className={cn("transition-colors rounded-full p-0.5", bgStyle ? 'opacity-70 hover:opacity-100' : 'text-slate-400 hover:text-indigo-500')} data-testid={`actual-info-${infoKey}`}>
                <Info className="h-3 w-3" />
              </button>
            )}
          </div>
          <p className={cn("text-2xl font-extrabold mt-1", !bgStyle && color)}>{value}</p>
          {subtext && <p className={cn("text-[10px] mt-0.5", bgStyle ? 'opacity-60' : 'text-slate-400')}>{subtext}</p>}
        </div>
        {showInfo && info && (
          <div className={cn("mt-2 p-2 rounded text-xs leading-relaxed space-y-1", bgStyle ? 'bg-white border border-slate-200 text-slate-900' : 'bg-indigo-50 border border-indigo-100')}>
            <p className={bgStyle ? '' : 'text-indigo-800'}><strong>What this shows:</strong> {info.what}</p>
            <p className={bgStyle ? 'opacity-90' : 'text-indigo-700'}><strong>How it's calculated:</strong> {info.how}</p>
            <p className={bgStyle ? 'opacity-80' : 'text-indigo-600'}><strong>Why it matters:</strong> {info.why}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ──── ROI Waterfall Explainer (collapsible) ────
function ROEWaterfallExplainer() {
  const [expanded, setExpanded] = useState(false);
  const steps = [
    { label: 'LEI', sublabel: 'Learning\nImprovement', color: 'bg-slate-700', question: 'How much did learning improve?', detail: 'LEI measures training quality, reach, and effectiveness across 15 measures.' },
    { label: 'C × M', sublabel: 'Capability ×\nMotivation', color: 'bg-blue-600', question: 'What skills & motivation changed?', detail: 'Capability (from L2 assessments) multiplied by Motivation (from surveys) gives PE Ideal.' },
    { label: 'PE Ideal', sublabel: 'People Engine\n(Predicted)', color: 'bg-purple-500', question: 'What is your predicted workforce capacity?', detail: 'PE Ideal = C × M. This is your projected workforce performance before real-world validation.' },
    { label: 'ROE', sublabel: 'Return on\nExpectation', color: 'bg-teal-600', question: 'Did capability become behavior?', detail: 'ROE = (0.4 × L2 Performance Shift) + (0.6 × L3 Stakeholder Impact). The reality multiplier.' },
    { label: 'PE Valid', sublabel: 'Validated\nCapacity', color: 'bg-purple-700', question: 'What capacity was actually transferred?', detail: 'PE Validated = PE Ideal × ROE. Your proven workforce capacity after behavior validation.' },
    { label: 'KPI', sublabel: 'Business\nMetrics', color: 'bg-emerald-600', question: 'Which business metrics moved?', detail: 'Validated PE maps to business KPIs (productivity, quality, safety, CSAT) through elasticity modeling.' },
    { label: 'ROI', sublabel: 'Financial\nReturn', color: 'bg-amber-600', question: 'What is the dollar value and return?', detail: 'Total KPI value per employee minus intervention cost = Net ROI.' },
  ];

  return (
    <Card className="border-slate-200 bg-gradient-to-b from-slate-50 to-white" data-testid="roe-waterfall-explainer">
      <CardContent className="pt-4 pb-3">
        <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between group" data-testid="roe-waterfall-toggle">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-teal-600" />
            <span className="text-sm font-semibold text-slate-800">Where ROE & PE Validated Fit in the ROI Waterfall</span>
          </div>
          {expanded ? <ChevronUp className="h-4 w-4 text-slate-400 group-hover:text-slate-600" /> : <ChevronDown className="h-4 w-4 text-slate-400 group-hover:text-slate-600" />}
        </button>
        {expanded && (
          <div className="mt-4 space-y-5" data-testid="roe-waterfall-content">
            <p className="text-sm text-slate-600 leading-relaxed">
              The ROI Waterfall connects every learning dollar to business outcomes.
              <strong> ROE Health</strong> shows <em>actual data</em> for the middle of the waterfall — validating whether predicted capability (PE Ideal) actually translated into real behavior change (PE Validated).
              The gap between PE Ideal and PE Validated is "capability leakage" — learning that was acquired but never applied.
            </p>
            <div className="overflow-x-auto pb-2">
              <div className="flex items-start gap-1 min-w-[700px]">
                {steps.map((step, i) => (
                  <React.Fragment key={step.label}>
                    <div className="flex flex-col items-center flex-1 min-w-0">
                      <div className={cn("w-full rounded-lg py-2 px-1 text-center text-white shadow-sm", step.color)}>
                        <p className="text-xs font-bold">{step.label}</p>
                      </div>
                      <div className="mt-2 text-center px-1">
                        <p className="text-[10px] text-slate-500 font-medium whitespace-pre-line leading-tight">{step.sublabel}</p>
                        <p className="text-[10px] text-slate-400 mt-1 italic">{step.question}</p>
                      </div>
                      <div className="mt-1.5 px-1 text-center">
                        <p className="text-[10px] text-slate-500 leading-snug">{step.detail}</p>
                      </div>
                    </div>
                    {i < steps.length - 1 && <div className="flex items-center pt-2.5 flex-shrink-0"><ArrowRight className="h-4 w-4 text-slate-300" /></div>}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-xs font-bold text-blue-800">PE Ideal = C × M</p>
                <p className="text-[10px] text-blue-600 mt-0.5">Predicted workforce capacity based on actual capability and motivation data.</p>
              </div>
              <div className="p-3 bg-teal-50 border border-teal-100 rounded-lg">
                <p className="text-xs font-bold text-teal-800">ROE = 0.4 × LPS + 0.6 × SI</p>
                <p className="text-[10px] text-teal-600 mt-0.5">The reality multiplier — how much of the predicted capacity actually became behavior.</p>
              </div>
              <div className="p-3 bg-purple-50 border border-purple-100 rounded-lg">
                <p className="text-xs font-bold text-purple-800">PE Validated = PE Ideal × ROE</p>
                <p className="text-[10px] text-purple-600 mt-0.5">Your proven workforce capacity. This is what actually drives KPI movement and financial ROI.</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ROEFilterBar({ organizations, filters, selectedOrg, selectedCohort, selectedProgram, onOrgChange, onCohortChange, onProgramChange }) {
  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="roe-filter-bar">
      <Select value={selectedOrg || '_all'} onValueChange={onOrgChange}>
        <SelectTrigger className="w-52" data-testid="roe-org-filter"><SelectValue placeholder="All Organizations" /></SelectTrigger>
        <SelectContent><SelectItem value="_all">All Organizations</SelectItem>{organizations.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
      </Select>
      {filters.cohorts.length > 0 && (
        <Select value={selectedCohort || '_all'} onValueChange={v => onCohortChange(v === '_all' ? '' : v)}>
          <SelectTrigger className="w-44" data-testid="roe-cohort-filter"><SelectValue placeholder="All Cohorts" /></SelectTrigger>
          <SelectContent><SelectItem value="_all">All Cohorts</SelectItem>{filters.cohorts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {filters.programs.length > 0 && (
        <Select value={selectedProgram || '_all'} onValueChange={v => onProgramChange(v === '_all' ? '' : v)}>
          <SelectTrigger className="w-52" data-testid="roe-program-filter"><SelectValue placeholder="All Programs" /></SelectTrigger>
          <SelectContent><SelectItem value="_all">All Programs</SelectItem>{filters.programs.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
      )}
    </div>
  );
}

function DataImportCards({ importing, onUpload, onDownload, focus }) {
  const types = [
    { key: 'l2', label: 'L2 Assessment Data', desc: 'Pre/post scores, skills uplift, certifications', icon: Target, color: 'blue' },
    { key: 'motivation', label: 'Motivation Surveys', desc: 'Intrinsic/extrinsic factors, confidence, manager support', icon: Zap, color: 'amber' },
    { key: 'l3', label: 'L3 Manager Observations', desc: 'Behavior change, skill application, performance', icon: Users, color: 'emerald' },
    { key: 'evidence', label: 'Application Evidence', desc: 'Self-report, manager & operational evidence of on-the-job application', icon: ClipboardCheck, color: 'rose' },
  ];
  const focusRef = React.useRef(null);
  React.useEffect(() => {
    if (focus?.type && focusRef.current) focusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focus?.type]);
  return (
    <div className="space-y-3">
      {focus?.type && (
        <div className="flex items-center gap-2 flex-wrap rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900" data-testid="import-focus-banner">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
          <span>
            Filling the <span className="font-semibold">{focus.label}</span> gap for cohort <span className="font-semibold" data-testid="import-focus-cohort">{focus.cohortName || focus.cohortId}</span> — the template below is pre-filled with its <code className="text-xs bg-amber-100 px-1 rounded">cohort_id</code>.
          </span>
          {focus.onClear && <button onClick={focus.onClear} className="ml-auto text-xs font-semibold text-amber-800 hover:text-amber-950" data-testid="import-focus-clear">Clear</button>}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="data-import-cards">
      {types.map(({ key, label, desc, icon: Icon, color }) => {
        const focused = focus?.type === key;
        return (
        <Card key={key} ref={focused ? focusRef : undefined} className={cn('border-dashed transition-shadow', focused && 'border-amber-400 border-solid ring-2 ring-amber-300/60 shadow-md animate-pulse [animation-iteration-count:3]')} data-testid={`import-card-${key}`} data-focused={focused ? 'true' : undefined}>
          <CardContent className="pt-4 text-center">
            <Icon className={cn("h-6 w-6 mx-auto mb-2", `text-${color}-500`)} />
            <p className="text-sm font-semibold text-slate-800">{label}</p>
            <p className="text-xs text-slate-500 mb-3">{desc}</p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" size="sm" onClick={() => onDownload(key)} data-testid={`download-template-${key}`}><Download className="h-3 w-3 mr-1" /> Template</Button>
              <label>
                <Button variant="outline" size="sm" disabled={importing === key} asChild>
                  <span className="cursor-pointer">{importing === key ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />} Import CSV</span>
                </Button>
                <input type="file" accept=".csv" className="hidden" onChange={e => e.target.files[0] && onUpload(key, e.target.files[0])} data-testid={`import-csv-${key}`} />
              </label>
            </div>
          </CardContent>
        </Card>
        );
      })}
      </div>
    </div>
  );
}

export default function ROEHealthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (location.hash !== '#action-plan') return;
    const t = setTimeout(() => document.getElementById('action-plan')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 400);
    return () => clearTimeout(t);
  }, [location.hash, location.pathname]);
  const [searchParams] = useSearchParams();
  const focusMetric = searchParams.get('focus');
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== 'light';
  const segs = location.pathname.split('/').filter(Boolean);
  let subView = segs[1] || 'overview';
  if (subView === 'actuals') subView = 'deep-dives';  // friendly alias
  if (!['overview', 'priorities', 'cohorts', 'deep-dives', 'variance', 'drivers', 'capabilities', 'influence', 'evidence', 'history'].includes(subView)) subView = 'overview';
  const { viewAsOrg, isSuperAdmin } = useAuth();
  // Deep-link support: a shared ?organization_id= URL scopes the page to that
  // org and (for super admins) aligns the global "Previewing as" banner. The
  // backend tenant guard still coerces non-super-admins to their own org.
  const urlOrgId = useDeepLinkOrg();
  const [tab, setTab] = useState('health');
  const [showCohortTools, setShowCohortTools] = useState(false);
  const [health, setHealth] = useState(null);
  const [variance, setVariance] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(null);
  const [evidenceRefresh, setEvidenceRefresh] = useState(0);
  const [filters, setFilters] = useState({ cohorts: [], programs: [] });
  const [selectedOrg, setSelectedOrg] = useState(urlOrgId || viewAsOrg?.id || '');
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedCohort, setSelectedCohort] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [organizations, setOrganizations] = useState([]);
  // Org-wide annual value at stake — powers the "If Not Addressed
  // / Lift" $ story under the weak-link callout. Same endpoint as
  // LEI / Maturity / PE so numbers reconcile across engines.
  const [valueAtStake, setValueAtStake] = useState(null);
  const roeQuarterDelta = useQuarterDelta('roe');

  const loadData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedOrg) params.set('organization_id', selectedOrg);
    if (selectedCohort) params.set('cohort_id', selectedCohort);
    if (selectedProgram) params.set('program_name', selectedProgram);
    const qs = params.toString();
    Promise.all([
      api.get(`/roe/actuals/health?${qs}`),
      api.get(`/roe/actuals/variance?${qs}`),
      api.get(`/roe/actuals/trend?${qs}`),
    ]).then(([hRes, vRes, tRes]) => {
      setHealth(hRes.data);
      setVariance(vRes.data);
      setTrend(tRes.data);
    }).catch(err => logger.error('ROE health error:', err))
      .finally(() => setLoading(false));
  }, [selectedOrg, selectedCohort, selectedProgram]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { api.get('/rbac/organizations').then(r => setOrganizations(r.data.organizations || [])).catch(() => {}); }, []);
  // Keep the deep-dive tab in sync with the subnav route.
  useEffect(() => {
    if (subView === 'variance') setTab('variance');
    else if (subView === 'deep-dives') setTab('health');
  }, [subView]);

  // Keep the page scoped to a ?organization_id= deep link if the param
  // changes while mounted (the initial value is seeded into selectedOrg).
  useEffect(() => {
    if (urlOrgId) setSelectedOrg(prev => (prev === urlOrgId ? prev : urlOrgId));
  }, [urlOrgId]);
  useEffect(() => {
    if (selectedOrg) api.get(`/roe/actuals/filters?organization_id=${selectedOrg}`).then(r => setFilters(r.data)).catch(() => {});    else setFilters({ cohorts: [], programs: [] });
  }, [selectedOrg]);

  // Fetch value-at-stake when the org changes — shared with
  // LEI / Maturity / PE so the dollar story reconciles.
  useEffect(() => {
    if (!selectedOrg) { setValueAtStake(null); return; }
    let cancelled = false;
    api.get(`/lei-health/value-at-stake?organization_id=${encodeURIComponent(selectedOrg)}`)
      .then((res) => {
        if (!cancelled && res?.data?.annual_value_at_stake_usd != null) {
          setValueAtStake(res.data);
        }
      })
      .catch(() => { /* silent — page still works without dollars */ });
    return () => { cancelled = true; };
  }, [selectedOrg]);

  const handleOrgChange = (v) => { setSelectedOrg(v === '_all' ? '' : v); setSelectedCohort(''); setSelectedProgram(''); };

  const handleCSVUpload = async (type, file) => {
    setImporting(type);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n').filter(l => !l.startsWith('#'));
      const headers = lines[0].split(',').map(h => h.trim());
      const rows = lines.slice(1).map(line => {
        const vals = line.split(',');
        const row = {};
        headers.forEach((h, i) => { row[h] = vals[i]?.trim() || ''; });
        return row;
      });
      const payload = { rows };
      if (selectedOrg) payload.organization_id = selectedOrg;
      const res = await api.post(`/roe/actuals/${type}/import`, payload);
      const { imported, skipped } = res.data || {};
      if (type === 'evidence') {
        if (skipped) toast.warning(`${imported} evidence rows imported · ${skipped} skipped (${res.data.skipped_rows?.[0]?.reason || 'invalid'})`);
        else toast.success(`${imported} evidence rows imported`);
        setEvidenceRefresh((k) => k + 1);
      } else {
        toast.success(`${rows.length} ${type.toUpperCase()} records imported`);
      }
      loadData();
    } catch (err) { toast.error(`Import failed: ${err.message}`); }
    finally { setImporting(null); }
  };

  const downloadTemplate = async (type) => {
    try {
      const cid = searchParams.get('cohort');
      const res = await api.get(`/roe/actuals/templates/${type}${cid ? `?cohort_id=${encodeURIComponent(cid)}` : ''}`);
      const { headers, example, description } = res.data;
      const csv = `# ${description}\n${headers.join(',')}\n${example.join(',')}`;
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `roe_${type}_template${cid ? `_${cid}` : ''}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  // Completeness "fix link" focus: /roe-health/deep-dives?import=<type>&cohort=<id>
  const importFocus = (() => {
    const type = searchParams.get('import');
    if (!type) return null;
    const cohortId = searchParams.get('cohort') || '';
    const label = { l2: 'Capability', motivation: 'Confidence', evidence: 'Application', l3: 'Behavior' }[type] || type;
    return { type, cohortId, cohortName: searchParams.get('cohort_name') || '', label, onClear: () => navigate('/roe-health/deep-dives') };
  })();

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-teal-500" /></div>;

  const theme = isDark
    ? { ...getSectionTheme('ROE'), heroGradient: 'bg-[#0a0f1c]', bodyTint: 'bg-[#0a0f1c]', textBase: 'text-white', textLighter: 'text-slate-400', titleClass: 'text-slate-50 font-extrabold' }
    : getLightSectionTheme('ROE');

  // Rich hero tiles — match Maturity Health / LEI Health parity.
  // Show only when actual data exists; otherwise the hero stays
  // simple and the body's "No ROE data — import below" coaches the
  // user.
  const h = health || {};
  const hasData = !!h.has_data;
  const roeActualPct = h.roe_actual?.pct || 0;
  const componentMetrics = hasData
    ? [
        { name: 'Capability', score: h.capability?.actual || 0, count: h.data_counts?.l2 || 0, unit: 'records' },
        { name: 'Motivation', score: h.motivation?.actual || 0, count: h.data_counts?.motivation || 0, unit: 'surveys' },
        { name: 'L3 Behavior', score: h.l3?.actual || 0, count: h.data_counts?.l3 || 0, unit: 'observations' },
      ]
    : [];
  const strongest = componentMetrics.reduce((acc, m) => (acc == null || m.score > acc.score ? m : acc), null);
  const topDeficiency = (h.deficiencies || [])
    .slice()
    .sort((a, b) => (b.severity === 'critical' ? 3 : b.severity === 'high' ? 2 : b.severity === 'medium' ? 1 : 0)
                  - (a.severity === 'critical' ? 3 : a.severity === 'high' ? 2 : a.severity === 'medium' ? 1 : 0))[0] || null;
  // Relative weak link surfaced only when there is no outright deficiency.
  const watchArea = !topDeficiency ? (h.watch_area || null) : null;

  // Option A treatment — white cards with section-color rings.
  // Status drives both the gauge stroke and the score text color
  // so health is single-encoded.
  const roeTone = roeActualPct >= 75
    ? { stroke: '#06b6d4', text: 'text-cyan-700', ring: 'ring-cyan-200', border: 'border-cyan-200', dot: 'bg-cyan-500', label: 'Strong' }
    : roeActualPct >= 50
      ? { stroke: '#14b8a6', text: 'text-teal-700', ring: 'ring-teal-200', border: 'border-teal-200', dot: 'bg-teal-500', label: 'Healthy' }
      : roeActualPct >= 25
        ? { stroke: '#f59e0b', text: 'text-amber-700', ring: 'ring-amber-200', border: 'border-amber-200', dot: 'bg-amber-500', label: 'At-Risk' }
        : { stroke: '#f43f5e', text: 'text-rose-700', ring: 'ring-rose-200', border: 'border-rose-200', dot: 'bg-rose-500', label: 'Critical' };

  const richHeroStats = hasData ? (
    <div
      className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mt-6"
      data-testid="roe-hero-rich-tiles"
    >
      {/* Actual ROE gauge — white card, status-colored stroke */}
      <Card
        className={cn('shadow-sm bg-white border border-slate-200 ring-1', roeTone.ring)}
        data-testid="roe-hero-actual"
      >
        <CardContent className="pt-5 pb-4 text-center">
          <div className="relative w-32 h-32 mx-auto mb-2">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
              <circle cx="50" cy="50" r="42" fill="none" stroke="#e2e8f0" strokeWidth="5" />
              <circle cx="50" cy="50" r="42" fill="none" stroke={roeTone.stroke} strokeWidth="5"
                strokeDasharray={`${Math.min(roeActualPct, 100) * 2.64} 264`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={cn('text-3xl font-extrabold leading-none', roeTone.text)}>{Math.round(roeActualPct)}%</span>
              <span className="text-[9px] text-slate-500 uppercase tracking-widest font-semibold mt-1">Actual ROE</span>
            </div>
          </div>
          <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-white', roeTone.border, roeTone.text)}>
            <span className={cn('w-1 h-1 rounded-full', roeTone.dot)} />
            {roeTone.label} Transfer
          </span>
          {roeQuarterDelta && (
            <div
              className={cn(
                'mt-2 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full',
                roeQuarterDelta.direction === 'up' && 'bg-emerald-50 text-emerald-700',
                roeQuarterDelta.direction === 'down' && 'bg-rose-50 text-rose-700',
                roeQuarterDelta.direction === 'flat' && 'bg-slate-50 text-slate-500',
              )}
              data-testid="roe-hero-quarter-delta"
              title={roeQuarterDelta.deltaText}
            >
              {roeQuarterDelta.direction === 'up' && '▲'}
              {roeQuarterDelta.direction === 'down' && '▼'}
              {roeQuarterDelta.direction === 'flat' && '▬'}
              {' '}{roeQuarterDelta.delta > 0 ? '+' : ''}{roeQuarterDelta.delta}% vs last qtr
            </div>
          )}
          {h.pe?.validated_pct != null && (
            <p className="text-[11px] text-slate-500 mt-1.5">
              PE Validated: <span className="font-semibold text-slate-700">{Math.round(h.pe.validated_pct)}%</span>
              {h.pe.transfer_gap_pct != null && <span className="text-slate-400"> · gap {h.pe.transfer_gap_pct}%</span>}
            </p>
          )}
          {h.peer_benchmark && (
            <p className="text-[11px] text-slate-500 mt-1" data-testid="roe-hero-peer">
              Peer median: <span className="font-semibold text-slate-700">{Math.round(h.peer_benchmark.peer_median)}%</span>
              <span className="text-slate-400"> · {ordinal(h.peer_benchmark.percentile)} pct · {h.peer_benchmark.industry_name}</span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Attention card — three states: outright Deficiency (rose),
          relative Watch Area (amber), or all-clear (emerald). */}
      {topDeficiency ? (
        <Card className="shadow-sm bg-white border border-slate-200 ring-1 ring-rose-200" data-testid="roe-hero-deficiency">
          <CardContent className="pt-6 pb-5 text-center">
            <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-3">
              <AlertTriangle className="h-5 w-5 text-rose-600" />
            </div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Top Deficiency</p>
            <p className="text-2xl font-extrabold text-slate-900 mt-1 truncate" title={topDeficiency.area}>{topDeficiency.area}</p>
            <p className="text-sm text-rose-700 mt-1.5 font-semibold uppercase tracking-wide">{topDeficiency.severity} severity</p>
            {topDeficiency.score != null && (
              <p className="text-xs text-slate-500 mt-1 tabular-nums">Score: {Math.round(topDeficiency.score)}%</p>
            )}
            <p className="text-xs text-slate-500 mt-1">
              {h.deficiencies?.length ? `${h.deficiencies.length} flagged area${h.deficiencies.length === 1 ? '' : 's'}` : 'Maintain the discipline'}
            </p>
          </CardContent>
        </Card>
      ) : watchArea ? (
        <Card className="shadow-sm bg-white border border-slate-200 ring-1 ring-amber-200" data-testid="roe-hero-watch">
          <CardContent className="pt-6 pb-5 text-center">
            <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
              <Eye className="h-5 w-5 text-amber-600" />
            </div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Watch Area</p>
            <p className="text-2xl font-extrabold text-slate-900 mt-1 truncate" title={watchArea.area} data-testid="roe-hero-watch-area">{watchArea.area}</p>
            <p className="text-sm text-amber-700 mt-1.5 font-semibold uppercase tracking-wide">Relative weak link</p>
            {watchArea.score != null && (
              <p className="text-xs text-slate-500 mt-1 tabular-nums">
                Score: {Math.round(watchArea.score)}%
                {watchArea.gap_vs_top != null && <span className="text-slate-400"> · {watchArea.gap_vs_top} pts below strongest</span>}
              </p>
            )}
            <p className="text-xs text-slate-500 mt-1">No deficiencies — monitor & reinforce</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="shadow-sm bg-white border border-slate-200 ring-1 ring-emerald-200" data-testid="roe-hero-deficiency">
          <CardContent className="pt-6 pb-5 text-center">
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
            </div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">No Deficiencies</p>
            <p className="text-2xl font-extrabold text-slate-900 mt-1 truncate" title="All measures within range">All Healthy</p>
            <p className="text-xs text-slate-500 mt-2">Maintain the discipline</p>
          </CardContent>
        </Card>
      )}

      {/* Strongest metric — white card, emerald accent */}
      <Card className="shadow-sm bg-white border border-slate-200 ring-1 ring-emerald-200" data-testid="roe-hero-strongest">
        <CardContent className="pt-6 pb-5 text-center">
          <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
            <Heart className="h-5 w-5 text-emerald-600" />
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Strongest Component</p>
          <p className="text-2xl font-extrabold text-slate-900 mt-1">{strongest?.name || '—'}</p>
          {strongest && (
            <p className="text-sm text-emerald-700 mt-1.5 font-semibold tabular-nums">Score: {Math.round(strongest.score)}%</p>
          )}
          {strongest && (
            <p className="text-xs text-slate-500 mt-1">
              {strongest.count.toLocaleString()} {strongest.unit}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  ) : null;

  return (
    <PageShell
      theme={theme}
      hero={(
        <PageHero
          title="Transfer Health"
          titleHint="ROE"
          description="See how effectively capability is being activated, applied and sustained — and where value is being amplified or eroded."
          theme={theme}
          icon={Heart}
          actions={(
            <>
              <PageToolbar theme={theme} collapsePrefix="roe" organizationId={selectedOrg} testId="roe-toolbar" />
              <Button variant="outline" size="sm" onClick={() => navigate('/roe-reference')} className="gap-1.5 bg-white border-slate-200 text-slate-700 hover:bg-slate-50">
                <BookOpen className="h-4 w-4" /> ROE Reference
              </Button>
              <DataAsOf organizationId={selectedOrg} onResynced={() => setRefreshKey((k) => k + 1)} testId="th-data-as-of" />
            </>
          )}
          stats={null}
          pinSnapshot={hasData ? [
            {
              label: 'Actual ROE',
              value: `${Math.round(roeActualPct)}%`,
              ...(roeQuarterDelta ? {
                delta: roeQuarterDelta.delta,
                deltaText: roeQuarterDelta.deltaText,
                deltaLabel: roeQuarterDelta.deltaLabel,
              } : {}),
            },
            { label: topDeficiency ? 'Top Deficiency' : watchArea ? 'Watch Area' : 'Top Deficiency', value: topDeficiency?.area || watchArea?.area || 'All Healthy' },
            { label: 'Strongest Component', value: strongest?.name || '—' },
          ] : null}
          testId="roe-hero"
        >
          <HeroAccents />
        </PageHero>
      )}
    >
      <div className={cn('space-y-6', isDark ? 'home-dark-scope' : 'mc-light')} data-testid="roe-health-page">
      <div className="hidden">
        <TileInfoButton
          testid="roe-page-info"
          title="ROE Health & the Kirkpatrick model"
          body="ROE Health is the seam between L2 and L3 — how much of what was learned actually became on-the-job behavior."
          side="bottom"
        />
      </div>

      <PageSubNav items={ROE_SUBNAV} />
      <div key={refreshKey} className="contents" data-testid="th-subview">

      {/* Overview — Transfer Health mission-control overview (mockup v1) */}
      {subView === 'overview' && (
        <TransferOverview selectedOrg={selectedOrg} selectedCohort={selectedCohort} isSuperAdmin={isSuperAdmin} />
      )}
      {subView === 'drivers' && <TransferDrivers selectedOrg={selectedOrg} selectedCohort={selectedCohort} />}
      {subView === 'capabilities' && <TransferCapabilities selectedOrg={selectedOrg} selectedCohort={selectedCohort} />}
      {subView === 'influence' && <TransferInfluence selectedOrg={selectedOrg} selectedCohort={selectedCohort} />}
      {subView === 'evidence' && <TransferEvidence selectedOrg={selectedOrg} selectedCohort={selectedCohort} navigate={navigate} />}
      {subView === 'history' && <TransferHistory selectedOrg={selectedOrg} selectedCohort={selectedCohort} />}
      {PENDING_SUBVIEWS[subView] && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid={`subview-pending-${subView}`}>
          <p className="text-base font-semibold text-slate-800">{PENDING_SUBVIEWS[subView]}</p>
          <p className="text-sm text-slate-500 mt-1">This page is being designed — mockup pending. The underlying data is already live in the detailed chain on the Overview.</p>
        </div>
      )}

      {/* Legacy lean overview (kept for the deep-dive route) */}
      {subView === 'lean-legacy' && hasData && (
        <RoeLeanOverview
          health={h}
          variance={variance?.variance}
          roeActualPct={roeActualPct}
          components={componentMetrics}
          strongest={strongest}
          topDeficiency={topDeficiency}
          watchArea={watchArea}
          valueAtStake={valueAtStake}
          selectedOrg={selectedOrg}
          selectedCohort={selectedCohort}
          isSuperAdmin={isSuperAdmin}
          navigate={navigate}
        />
      )}

      {/* Cohorts — per-cohort Capability → Confidence → Application → Behavior */}
      {subView === 'cohorts' && (
        <div className="space-y-4" data-testid="roe-cohorts-view">
          <TransferCohorts selectedOrg={selectedOrg} selectedCohort={selectedCohort} />
          <button type="button" onClick={() => setShowCohortTools((v) => !v)} className="text-xs font-semibold text-blue-600 hover:underline" data-testid="co-toggle-tools">{showCohortTools ? 'Hide detailed cohort tools' : 'Show detailed cohort tools'}</button>
          {showCohortTools && (<>
          <DataGapsStrip selectedOrg={selectedOrg} navigate={navigate} />
          <CohortTransferTable
            navigate={navigate}
            selectedOrg={selectedOrg}
            topN={12}
            period={searchParams.get('period')}
            onClearPeriod={() => navigate('/roe-health/cohorts')}
            onPeriodChange={(p) => navigate(p ? `/roe-health/cohorts?period=${encodeURIComponent(p)}` : '/roe-health/cohorts')}
          />
          </>)}
        </div>
      )}

      {(subView === 'priorities') && (
      <>
      {/* Gap Coaching — a material Application Transfer Gap becomes an
          actionable plan, independent of whether a modelled baseline exists. */}
      {hasData && h.application_detail?.coaching && (
        <div className="mb-6" data-testid="roe-gap-coaching-section">
          <h2 className="text-lg font-bold text-slate-900">Transfer Gap Coaching</h2>
          <p className="text-sm text-slate-500 mb-3">Readiness that isn’t converting into on-the-job application — and how to close it</p>
          <GapCoachingCard detail={h.application_detail} navigate={navigate} organizationId={selectedOrg || undefined} trend={trend?.synthetic ? [] : (trend?.trend || [])} />
        </div>
      )}
      {/* Priorities — Risk Areas & Action Plan. Its own sub-nav page;
          identifies the metric with the biggest negative variance
          (modelled vs actual) and pairs it with a hybrid action plan. */}
      {hasData && Array.isArray(variance?.variance) && variance.variance.length > 0 && (() => {
        // ROE variance rows are {metric, modelled, actual, variance}.
        // Pick the metric with the most negative variance — that's
        // where actuals fell furthest below the plan.
        const ROE_METRIC_KEYS = {
          'Capability': 'capability',
          'Motivation': 'motivation',
          'L3 Behavior': 'l3_behavior',
          'PE Score': 'pe_score',
          'ROE': 'roe',
        };
        const rows = variance.variance.filter((r) => ROE_METRIC_KEYS[r.metric] != null);
        if (rows.length === 0) return null;
        const worst = rows.reduce(
          (lo, r) => (r.variance < lo.variance ? r : lo),
          rows[0],
        );
        const metricKey = ROE_METRIC_KEYS[worst.metric];
        // ROE's metric is a signed variance in pts. We map it to
        // the same realized-% framing used by LEI / Maturity / PE
        // so the dollar story reconciles across engines:
        //   realizedPct   = max(0, 100 − |variance|)
        //   afterFixPct   = min(100, realizedPct + 15)
        //   deltaPct      = afterFixPct − realizedPct
        const LIFT_POINTS = 15;
        const absVar = Math.abs(worst.variance || 0);
        const realizedPct = Math.max(0, Math.min(100, 100 - absVar));
        const afterFixPct = Math.min(100, realizedPct + LIFT_POINTS);
        const deltaPct = afterFixPct - realizedPct;
        const vas = valueAtStake?.annual_value_at_stake_usd ?? null;
        const currency = valueAtStake?.currency || 'USD';
        const financialImpact = {
          realizedPct,
          afterFixPct,
          deltaPct,
          currency,
          liftLabel: `Close ${worst.metric} variance by ${LIFT_POINTS}pts`,
          dollarsLeaking: vas != null ? vas * (1 - realizedPct / 100) : null,
          dollarsUnlocked: vas != null ? vas * (deltaPct / 100) : null,
          leakCopy: `Every quarter you leave a ${worst.variance > 0 ? '+' : ''}${worst.variance} pt gap between modelled and actual ${worst.metric}, roughly ${Math.round(absVar)}% of the ROE story goes unrealised.`,
          fixCopy: `Closing the ${worst.metric} variance by ${LIFT_POINTS}pts shifts realized ROE by ${Math.round(deltaPct)} percentage points.`,
          vasFootnote: valueAtStake ? {
            revenue: valueAtStake.inputs?.annual_revenue,
            marginPct: valueAtStake.inputs?.operating_margin_pct,
            isDefault: !!valueAtStake.is_default,
          } : null,
        };
        return (
          <div className="space-y-4" id="action-plan" data-testid="roe-priority-focus-section">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Risk Areas &amp; Action Plan</h2>
              <p className="text-sm text-slate-500">Biggest risk to close + your recommended action plan</p>
            </div>
            <div className="space-y-3">
            <EngineWeakLinkCallout
              entityLabel={`${worst.metric} variance`}
              score={worst.variance}
              overall={roeActualPct}
              metricSuffix="pts"
              signed
              description="has the biggest gap between modelled and actual."
              testIdPrefix="roe-weak-link"
              financialImpact={financialImpact}
              modelFixCta={{
                label: `Close ${worst.metric} variance in Portfolio Analysis`,
                onClick: () => {
                  navigate('/portfolio', {
                    state: {
                      focusReason: 'roe-weak-link',
                      focusROEMetric: worst.metric,
                      focusROEMetricKey: metricKey,
                      focusROELiftPoints: 15,
                      autoPin: true,
                    },
                  });
                  toast.info(
                    `Adjust the L&D measures most likely to close the ${worst.metric} variance, then click Calculate — the scenario will auto-pin to your Quarterly Deck.`,
                  );
                },
              }}
            />
            <EngineActionPlanCard
              engine="roe"
              params={{
                metric: metricKey,
                organization_id: selectedOrg,
                cohort_id: selectedCohort !== 'all' ? selectedCohort : undefined,
                score: worst.variance,
                overall_score: roeActualPct,
              }}
            />
            </div>
          </div>
        );
      })()}
      {!(hasData && Array.isArray(variance?.variance) && variance.variance.length > 0) && !(hasData && h.application_detail?.coaching) && (
        <div className="text-center py-12" data-testid="roe-priorities-empty">
          <AlertTriangle className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-slate-800 mb-2">No Priorities Yet</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">Import ROE actuals (Actuals tab) and run an ROE model to surface your biggest risk areas and a recommended action plan here.</p>
        </div>
      )}
      </>
      )}

      {/* Actuals & Variance deep dives — each its own sub-nav page */}
      {(subView === 'deep-dives' || subView === 'variance') && (
      <>
      {/* Filters — cohort scoped */}
      <div className="mb-4 flex" data-testid="roe-deepdive-controls">
        <Select value={selectedCohort || '_all'} onValueChange={(v) => setSelectedCohort(v === '_all' ? '' : v)}>
          <SelectTrigger className="w-[220px]" data-testid="roe-cohort-filter"><SelectValue placeholder="All Cohorts" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Cohorts</SelectItem>
            {(filters.cohorts || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {subView === 'variance' ? (
        <div data-testid="roe-variance-view">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <GitCompare className="h-5 w-5 text-slate-700" />
              <h2 className="text-lg font-bold text-slate-900">Explore the Variances</h2>
              <TileInfoButton testid="tab-info-variance" title={TAB_INFO.variance.title} body={TAB_INFO.variance.body} />
            </div>
            <Button onClick={() => navigate('/roe-modeller')} data-testid="model-roe-btn"
              className="!bg-[#14b8a6] !text-white hover:!bg-[#0d9488] !border-transparent shrink-0">
              <Target className="h-4 w-4 mr-2" /> Model ROE
            </Button>
          </div>
          {variance?.has_model === false ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center" data-testid="roe-variance-no-model">
              <GitCompare className="h-8 w-8 text-amber-400 mx-auto mb-2" />
              <h3 className="text-base font-semibold text-amber-900 mb-1">No modelled baseline yet</h3>
              <p className="text-sm text-amber-800 max-w-md mx-auto">Run an ROE model for this selection (Model ROE) to unlock the modelled-vs-actual variance chart.</p>
            </div>
          ) : (
            <VarianceContent data={variance} focusMetric={focusMetric} />
          )}
        </div>
      ) : (
        <div data-testid="roe-actuals-view">
          <div className="flex items-center gap-2 mb-3">
            <Heart className="h-5 w-5 text-slate-700" />
            <h2 className="text-lg font-bold text-slate-900">Explore the Actuals</h2>
            <TileInfoButton testid="tab-info-health" title={TAB_INFO.health.title} body={TAB_INFO.health.body} />
          </div>
          {!health?.has_data ? (
            <div className="space-y-6">
              <div className="text-center py-8">
                <Heart className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-slate-800 mb-2">No ROE Actuals Data</h3>
                <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">{selectedOrg ? 'No data for this organization. ' : ''}Import L2, Motivation, and L3 data below.</p>
              </div>
              <DataImportCards importing={importing} onUpload={handleCSVUpload} onDownload={downloadTemplate} focus={importFocus} />
              <EvidenceImportsTable selectedOrg={selectedOrg} refreshKey={evidenceRefresh} onChanged={loadData} />
            </div>
          ) : (
            <HealthContent health={health} variance={variance} trend={trend} selectedOrg={selectedOrg} selectedCohort={selectedCohort} selectedProgram={selectedProgram} onSelectCohort={setSelectedCohort} importing={importing} onUpload={handleCSVUpload} onDownload={downloadTemplate} evidenceRefresh={evidenceRefresh} onEvidenceChanged={loadData} importFocus={importFocus} />
          )}
          {trend?.has_data && (
            <div className="mt-6" data-testid="roe-trend">
              <InteractiveTrendChart
                title="ROE Trend (6 months)"
                data={trend.trend}
                xKey="month"
                valueFormatter={(v) => `${v}%`}
                height={300}
                testId="roe-trend-chart"
                hint={trend.synthetic ? 'Click legend to toggle' : 'Click legend to toggle · click a month to drill into cohorts'}
                onPointClick={trend.synthetic ? undefined : (row) => {
                  if (row?.period) navigate(`/roe-health/cohorts?period=${encodeURIComponent(row.period)}`);
                }}
                note={trend.synthetic
                  ? 'Modelled history — a projected 6-month path ending at your current actuals (no monthly history available for this selection).'
                  : 'Real monthly actuals — each point is that month’s L2 / Motivation / L3 composite (the headline ROE above is the all-period average, so it won’t match the latest month exactly).'}
                series={[
                  { key: 'roe', name: 'Actual ROE', color: isDark ? '#f8fafc' : '#0f172a', primary: true },
                  { key: 'capability', name: 'Capability', color: '#3b82f6' },
                  { key: 'motivation', name: 'Motivation', color: '#f59e0b' },
                  { key: 'l3', name: 'L3 Behavior', color: '#8b5cf6' },
                ]}
              />
            </div>
          )}
          {trend?.has_data && (trend.trend || []).length >= 2 && (() => {
            const rows = trend.trend;
            const f = rows[0], l = rows[rows.length - 1];
            const num = (v) => (typeof v === 'number' ? v : null);
            const composite = num(l.roe) == null ? null : { now: l.roe, delta: (l.roe || 0) - (f.roe || 0) };
            const components = [
              { name: 'Capability', now: num(l.capability), delta: (l.capability || 0) - (f.capability || 0) },
              { name: 'Motivation', now: num(l.motivation), delta: (l.motivation || 0) - (f.motivation || 0) },
              { name: 'L3 Behavior', now: num(l.l3), delta: (l.l3 || 0) - (f.l3 || 0) },
            ].filter((c) => c.now != null);
            return (
              <div className="mt-6">
                <TrendInsightNarrator
                  engine="roe" engineLabel="ROE" compositeLabel="ROE"
                  organizationId={selectedOrg} cohortId={selectedCohort || 'all'}
                  composite={composite} components={components}
                  testId="roe-trends-insight"
                />
              </div>
            );
          })()}
        </div>
      )}
      </>
      )}
      </div>
      </div>
    </PageShell>
  );
}

function HealthContent({ health, variance, trend, selectedOrg, selectedCohort, selectedProgram, onSelectCohort, importing, onUpload, onDownload, evidenceRefresh, onEvidenceChanged, importFocus }) {
  const h = health;
  return (
    <div className="space-y-6">

      {h.deficiencies.length > 0 && (
        <Card data-testid="roe-deficiencies">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-rose-500" /> Deficiency Analysis</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {h.deficiencies.map(d => (
              <div key={d.area} className={cn("p-3 rounded-lg border", severityStyles[d.severity]?.card)}>
                <div className="flex items-center gap-2 mb-1"><Badge className={cn("text-xs", severityStyles[d.severity]?.badge)}>{d.severity}</Badge><span className={cn("text-sm font-semibold", severityStyles[d.severity]?.text)}>{d.area}</span><span className="text-2xl font-bold ml-auto" style={{ color: d.score < 30 ? '#ef4444' : d.score < 50 ? '#f59e0b' : '#14b8a6' }}>{d.score}%</span></div>
                <p className="text-sm text-slate-700">{d.message}</p>
                <p className="text-xs text-slate-500 mt-1"><strong>Action:</strong> {d.action}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <ComponentSummary
        health={h}
        variance={variance}
        trend={trend}
        selectedOrg={selectedOrg}
        selectedCohort={selectedCohort}
        selectedProgram={selectedProgram}
        onSelectCohort={onSelectCohort}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DetailCardL2 h={h} />
        <DetailCardMotivation h={h} />
        <DetailCardL3 h={h} />
      </div>

      <DataImportCards importing={importing} onUpload={onUpload} onDownload={onDownload} focus={importFocus} />
      <EvidenceImportsTable selectedOrg={selectedOrg} refreshKey={evidenceRefresh} onChanged={onEvidenceChanged} />
    </div>
  );
}

// Tiny inline SVG sparkline (theme-agnostic, colored stroke).
function Sparkline({ values, color = '#64748b', width = 96, height = 28 }) {
  const nums = (values || []).map((v) => (typeof v === 'number' ? v : Number(v))).filter((v) => !Number.isNaN(v));
  if (nums.length < 2) return null;
  const min = Math.min(...nums), max = Math.max(...nums);
  const span = max - min || 1;
  const step = width / (nums.length - 1);
  const pts = nums.map((v, i) => `${(i * step).toFixed(1)},${(height - ((v - min) / span) * height).toFixed(1)}`).join(' ');
  const last = nums[nums.length - 1];
  const lx = (nums.length - 1) * step;
  const ly = height - ((last - min) / span) * height;
  return (
    <svg width={width} height={height} className="overflow-visible" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lx} cy={ly} r="2" fill={color} />
    </svg>
  );
}

function DeltaChip({ actual, modelled, testId }) {
  if (actual == null || modelled == null) {
    return <span className="text-[11px] text-slate-400" data-testid={testId}>no model</span>;
  }
  const delta = Math.round((actual - modelled) * 10) / 10;
  const up = delta >= 0;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
        up ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700',
      )}
      title={`Actual ${actual}% vs modelled ${modelled}%`}
      data-testid={testId}
    >
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {up ? '+' : ''}{delta} vs modelled
    </span>
  );
}

function TargetChip({ actual, target, testId }) {
  if (actual == null || target == null) return null;
  const gap = Math.round((actual - target) * 10) / 10;
  const met = gap >= 0;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
        met ? 'bg-teal-100 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300'
          : 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
      )}
      title={`Actual ${actual}% vs target ${target}%`}
      data-testid={testId}
    >
      <Target className="h-3 w-3" />
      {met ? 'target met' : `${gap} vs target`}
    </span>
  );
}

const ROE_COMPONENT_META = {
  Capability: { key: 'capability', color: '#3b82f6', icon: Target, trendKey: 'capability' },
  Motivation: { key: 'motivation', color: '#f59e0b', icon: Zap, trendKey: 'motivation' },
  'L3 Behavior': { key: 'l3', color: '#10b981', icon: Users, trendKey: 'l3' },
};

function ComponentSummary({ health, variance, trend, selectedOrg, selectedCohort, selectedProgram, onSelectCohort }) {
  const [sortByGap, setSortByGap] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [cohorts, setCohorts] = useState(null);
  const [loadingCohorts, setLoadingCohorts] = useState(false);
  const [targets, setTargets] = useState({});

  useEffect(() => {
    let alive = true;
    const q = selectedOrg ? `?organization_id=${selectedOrg}` : '';
    api.get(`/roe/actuals/targets${q}`)
      .then((r) => { if (alive) setTargets(r?.data?.targets || {}); })
      .catch(() => { if (alive) setTargets({}); });
    return () => { alive = false; };
  }, [selectedOrg]);

  const vmap = {};
  const hasModel = variance?.has_model !== false;
  if (hasModel) (variance?.variance || []).forEach((v) => { vmap[v.metric] = v; });

  let comps = Object.entries(ROE_COMPONENT_META).map(([name, meta]) => {
    const actual = health?.[meta.key]?.actual ?? null;
    const modelled = vmap[name]?.modelled ?? null;
    // Single source of truth: gap = actual − modelled (same basis the
    // DeltaChip renders), so the card can't show two conflicting numbers.
    const gap = (actual != null && modelled != null) ? Math.round((actual - modelled) * 10) / 10 : null;
    return { name, meta, actual, modelled, gap };
  });
  if (sortByGap) {
    comps = [...comps].sort((a, b) => (a.gap ?? 0) - (b.gap ?? 0));
  }

  const loadCohorts = async () => {
    if (expanded) { setExpanded(false); return; }
    setExpanded(true);
    if (cohorts) return;
    setLoadingCohorts(true);
    try {
      const params = new URLSearchParams();
      if (selectedOrg) params.set('organization_id', selectedOrg);
      if (selectedProgram) params.set('program_name', selectedProgram);
      const res = await api.get(`/roe/actuals/cohort-breakdown?${params.toString()}`);
      setCohorts(res.data?.cohorts || []);
    } catch {
      setCohorts([]);
    } finally {
      setLoadingCohorts(false);
    }
  };

  return (
    <Card data-testid="roe-component-summary">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2"><GitCompare className="h-4 w-4 text-indigo-600" /> Component Scores vs Modelled</CardTitle>
        <button
          onClick={() => setSortByGap((s) => !s)}
          data-testid="roe-summary-sort-toggle"
          className={cn('inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold transition-colors', sortByGap ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}
        >
          <ArrowUpDown className="h-3 w-3" /> {sortByGap ? 'Biggest gap first' : 'Sort by gap'}
        </button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {comps.map(({ name, meta, actual, modelled, gap }) => {
            const Icon = meta.icon;
            const spark = (trend?.trend || []).map((r) => r[meta.trendKey]).filter((v) => v != null);
            return (
              <div key={name} className="rounded-lg border border-slate-200 p-3" data-testid={`roe-summary-card-${meta.key}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-600 flex items-center gap-1"><Icon className="h-3.5 w-3.5" style={{ color: meta.color }} /> {name}</span>
                </div>
                <div className="mt-1 flex items-end justify-between">
                  <span className="text-2xl font-extrabold tabular-nums" style={{ color: meta.color }}>{actual != null ? `${actual}%` : '—'}</span>
                  {spark && spark.length >= 2 && <Sparkline values={spark} color={meta.color} />}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <DeltaChip actual={actual} modelled={modelled} testId={`roe-summary-delta-${meta.key}`} />
                  <TargetChip actual={actual} target={targets?.[meta.key]} testId={`roe-summary-target-${meta.key}`} />
                </div>
              </div>
            );
          })}
        </div>

        <div>
          <button
            onClick={loadCohorts}
            data-testid="roe-cohort-breakdown-toggle"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
          >
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} Cohort breakdown
          </button>
          {expanded && (
            <div className="mt-2" data-testid="roe-cohort-breakdown">
              {loadingCohorts ? (
                <p className="text-xs text-slate-400 py-2">Loading cohorts…</p>
              ) : !cohorts || cohorts.length === 0 ? (
                <p className="text-xs text-slate-400 py-2">No per-cohort data available for this selection.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-slate-400 border-b border-slate-100 text-left">
                        <th className="py-1.5 pr-3 font-semibold">Cohort</th>
                        <th className="py-1.5 px-2 font-semibold text-right">Capability</th>
                        <th className="py-1.5 px-2 font-semibold text-right">Motivation</th>
                        <th className="py-1.5 px-2 font-semibold text-right">L3 Behavior</th>
                        <th className="py-1.5 pl-2 font-semibold text-right">ROE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cohorts.map((c) => (
                        <tr key={c.cohort_id} onClick={() => onSelectCohort?.(c.cohort_id)} title="View this cohort's Actuals" className="border-b border-slate-50 cursor-pointer hover:bg-indigo-50/60 transition-colors" data-testid={`roe-cohort-row-${c.cohort_id}`}>
                          <td className="py-1.5 pr-3 font-medium text-indigo-700 underline decoration-dotted">{c.cohort_name}</td>
                          {['capability', 'motivation', 'l3', 'roe'].map((k) => (
                            <td key={k} className="py-1.5 px-2 text-right tabular-nums">
                              <span className="font-semibold text-slate-800">{c[k]?.actual != null ? `${c[k].actual}%` : '—'}</span>
                              {c[k]?.variance != null && (
                                <span className={cn('ml-1 text-[10px]', c[k].variance >= 0 ? 'text-emerald-600' : 'text-rose-600')}>({c[k].variance >= 0 ? '+' : ''}{c[k].variance})</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function MetricRow({ label, value, bold, color, info }) {
  const [showInfo, setShowInfo] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-slate-500 flex items-center gap-1">
          {label}
          {info && <button onClick={() => setShowInfo(!showInfo)} className="text-slate-400 hover:text-indigo-500 transition-colors"><Info className="h-3 w-3" /></button>}
        </span>
        <span className={cn("font-medium", bold && "font-bold", color)}>{value}</span>
      </div>
      {showInfo && info && (
        <div className="mt-1 mb-1.5 p-2 bg-indigo-50 border border-indigo-100 rounded text-xs leading-relaxed space-y-0.5">
          <p className="text-indigo-800"><strong>What this shows:</strong> {info.what}</p>
          <p className="text-indigo-700"><strong>Impact:</strong> {info.impact}</p>
        </div>
      )}
    </div>
  );
}

function DetailCardL2({ h }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-xs flex items-center gap-1"><Target className="h-3.5 w-3.5 text-blue-600" /> L2: Capability Detail</CardTitle></CardHeader>
      <CardContent className="space-y-1.5 text-sm">
        <MetricRow label="Pre-Score Avg" value={`${h.capability.pre_avg}%`} info={{
          what: "Average assessment score before training across all programs and cohorts.",
          impact: "The baseline capability level. Low pre-scores mean more room for improvement but may also indicate readiness gaps that limit learning absorption."
        }} />
        <MetricRow label="Post-Score Avg" value={`${h.capability.post_avg}%`} info={{
          what: "Average assessment score after training. The higher the post-score, the more knowledge was acquired.",
          impact: "Directly feeds the Capability (C) component of PE. Higher post-scores increase C, which multiplies with Motivation to determine PE."
        }} />
        <MetricRow label="Skills Uplift" value={`${h.capability.uplift_avg}%`} bold color="text-blue-600" info={{
          what: "The percentage gain from pre to post assessment — the core L2 measure of whether training built capability.",
          impact: "This is the strongest signal that learning created capability. Low uplift despite high post-scores suggests learners already knew the material (wrong audience). Low uplift with low post-scores means the training design needs improvement. Directly drives ROE through the Learner Performance Shift (LPS) calculation."
        }} />
        <MetricRow label="Cert Pass Rate" value={`${h.capability.cert_avg}%`} info={{
          what: "Percentage of learners achieving certification or proficiency standards.",
          impact: "Certifications provide validated, defensible proof of capability. Low pass rates indicate the training isn't reaching proficiency thresholds — learners know more but can't yet perform. This dampens the C score and reduces PE."
        }} />
        <MetricRow label="30-day Retention" value={`${h.capability.retention_avg}%`} info={{
          what: "Knowledge retained 30 days after training, measured through follow-up assessments.",
          impact: "Without retention, capability is temporary. If retention drops significantly from post-score, it means learning decays before it can change behavior — breaking the C → ROE → KPI chain. Low retention = you're paying for capability that evaporates before driving ROI."
        }} />
      </CardContent>
    </Card>
  );
}

function DetailCardMotivation({ h }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-xs flex items-center gap-1"><Zap className="h-3.5 w-3.5 text-amber-500" /> Motivation Detail</CardTitle></CardHeader>
      <CardContent className="space-y-1.5 text-sm">
        <MetricRow label="Intrinsic Score" value={`${h.motivation.intrinsic_avg}%`} info={{
          what: "Average of purpose, mastery, and autonomy motivation factors — the internal drive to learn and apply.",
          impact: "Intrinsic motivation is the strongest predictor of sustained behavior change. People driven by purpose and mastery apply learning because they want to, not because they're told to. Low intrinsic motivation means even great training won't stick — directly reducing M in the PE equation."
        }} />
        <MetricRow label="Extrinsic Score" value={`${h.motivation.extrinsic_avg}%`} info={{
          what: "Average of recognition, career growth, and incentive motivation factors — external drivers to apply learning.",
          impact: "Extrinsic motivation provides short-term application pressure. Useful for compliance-driven change but fades without reinforcement. If extrinsic significantly outweighs intrinsic, behavior change may not be sustained long-term."
        }} />
        <MetricRow label="Confidence to Apply" value={`${h.motivation.confidence_avg}%`} bold color="text-amber-600" info={{
          what: "Learners' self-reported confidence that they can apply new skills on the job.",
          impact: "This is the critical bridge between knowing and doing. Even highly capable, motivated learners won't change behavior if they lack confidence. Low confidence is the #1 hidden barrier to ROE — it directly reduces the Learner Performance Shift (LPS) and suppresses the entire C × M → ROE chain."
        }} />
        <MetricRow label="Manager Support" value={`${h.motivation.manager_support_avg}%`} info={{
          what: "The degree to which managers actively reinforce and support learning application on the job.",
          impact: "Research shows manager reinforcement is the single biggest predictor of training transfer (80-90% of transfer depends on it). Low manager support means most learning investment is wasted regardless of capability gains. This is often the most impactful lever to pull for improving ROE."
        }} />
      </CardContent>
    </Card>
  );
}

function DetailCardL3({ h }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-xs flex items-center gap-1"><Users className="h-3.5 w-3.5 text-emerald-600" /> L3: Behavior Transfer</CardTitle></CardHeader>
      <CardContent className="space-y-1.5 text-sm">
        <MetricRow label="L3 Score" value={`${h.l3.actual}%`} bold color="text-emerald-600" info={{
          what: "Composite score from manager observations: behavior change (35%), skill application (30%), performance improvement (20%), manager confidence (15%).",
          impact: "This is the definitive measure of whether learning actually changed workplace behavior. L3 carries 60% weight in the ROE calculation. Low L3 means the entire value chain breaks — capability was built but never applied, so KPI impact cannot materialize. This is where most L&D ROI is lost."
        }} />
        <MetricRow label="Observations" value={h.l3.count} info={{
          what: "Number of structured L3 manager observation records submitted.",
          impact: "Sample size matters for confidence. Fewer than 10 observations per cohort means results may not be representative. More observations strengthen the statistical validity of your ROE score and make it more defensible to stakeholders."
        }} />
        <MetricRow label="LPS (L2)" value={`${h.lps_actual}%`} info={{
          what: "Learner Performance Shift — (0.6 × Skills Uplift) + (0.4 × Confidence to Apply). The L2 component of ROE.",
          impact: "LPS measures whether learners changed in knowledge AND confidence. It carries 40% weight in the ROE calculation. High LPS with low L3 means people learned and feel ready, but aren't actually applying — look at manager support and environmental barriers."
        }} />
        <MetricRow label="SI (L3)" value={`${h.si_actual}%`} info={{
          what: "Stakeholder Impact — the weighted L3 score from manager observations. Carries 60% weight in ROE.",
          impact: "SI is the reality check. It validates whether the LPS (learning shift) actually translated to observable performance change. SI directly determines whether KPI movement can be attributed to learning. Low SI = your ROI story has no evidence."
        }} />
      </CardContent>
    </Card>
  );
}

const varianceInfoMap = {
  'Capability': {
    what: 'Compares the modelled capability shift (based on projected pre/post assessment gains) against the actual L2 assessment results.',
    impact: 'If actual capability is lower than modelled, it means training didn\'t build as much knowledge as expected — reducing the C component of PE and suppressing all downstream ROE/KPI calculations.',
    potential: 'Closing this gap improves the foundation of the entire value chain. Every 1% improvement in C directly multiplies through to PE and ROI.',
    next: 'Review training design quality, audience targeting (were the right people trained?), and assessment alignment. Consider spaced reinforcement to boost retention.'
  },
  'Motivation': {
    what: 'Compares the modelled motivation assumption against actual survey data combining intrinsic drive, extrinsic incentives, confidence to apply, and manager support.',
    impact: 'Motivation is the multiplier. Even excellent capability gains are wasted if motivation is lower than expected — people simply won\'t apply what they learned.',
    potential: 'Motivation gaps are often the most cost-effective to close because they don\'t require retraining — just better reinforcement, manager engagement, and environmental support.',
    next: 'Focus on manager support scores (the #1 predictor of transfer). Build post-training accountability structures and coaching touchpoints.'
  },
  'L3 Behavior': {
    what: 'Compares projected behavior transfer against actual manager-observed behavior change, skill application, and performance improvement on the job.',
    impact: 'L3 carries 60% weight in the ROE calculation. A negative variance here means the biggest value leakage in your learning investment — people learned but aren\'t doing.',
    potential: 'Improving L3 has the highest ROI impact of any single lever because it directly validates that learning investment is producing business behavior change.',
    next: 'Increase observation frequency, implement 30/60/90-day check-ins, create peer accountability groups, and ensure managers are actively coaching application.'
  },
  'PE Score': {
    what: 'Compares the projected People Engine capacity transfer against the actual PE Validated score (PE Ideal × ROE).',
    impact: 'PE Score is the bottom line of human capital change. A variance here represents the gap between what your learning investment should have produced versus what it actually did.',
    potential: 'PE Score variance is a composite indicator — fixing upstream C, M, or L3 gaps will automatically improve PE Score.',
    next: 'Identify which upstream component (C, M, or L3) has the largest variance and prioritize that. Use the ROE Modeller to simulate the impact of closing specific gaps.'
  },
  'ROE': {
    what: 'Compares the overall modelled Return on Expectation against the actual ROE calculated from real L2, Motivation, and L3 data.',
    impact: 'ROE is the reality multiplier for all PE projections. A negative ROE variance means your financial projections (KPI/ROI) are overstated relative to actual learning transfer.',
    potential: 'Accurate ROE builds stakeholder trust. Closing the ROE variance gap makes your business case projections defensible and credible.',
    next: 'Use the ROE Modeller to run scenarios on which levers (C, M, L3) would most efficiently close this gap. Focus on the component with the highest variance first.'
  }
};

function VarianceDetailRow({ v, focus }) {
  const [showInfo, setShowInfo] = useState(!!focus);
  const rowRef = useRef(null);
  useEffect(() => {
    if (focus && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focus]);
  const isPos = v.variance >= 0;
  const isSig = Math.abs(v.variance) > 10;
  const info = varianceInfoMap[v.metric];
  return (
    <div ref={rowRef} key={v.metric} className={cn("rounded-lg border transition-all", focus ? 'border-rose-400 ring-2 ring-rose-300 bg-rose-50/40' : isSig && !isPos ? 'border-rose-200 bg-rose-50/30' : 'border-slate-100')} data-focused={focus ? 'true' : undefined}>
      <div className="flex items-center justify-between p-3" data-testid={`variance-row-${v.metric}`}>
        <div className="flex items-center gap-3">
          <div className={cn("w-1.5 h-8 rounded-full", isPos ? 'bg-teal-400' : v.variance < -10 ? 'bg-rose-400' : 'bg-amber-400')} />
          <div>
            <p className="text-sm font-semibold text-slate-800">{v.metric}</p>
            <p className="text-xs text-slate-500">Modelled: {v.modelled}% | Actual: {v.actual}%</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {info && (
            <button onClick={() => setShowInfo(!showInfo)} className={cn("p-1 rounded-full transition-colors", showInfo ? 'bg-indigo-100 text-indigo-600' : 'text-slate-400 hover:text-indigo-500 hover:bg-indigo-50')} data-testid={`variance-info-toggle-${v.metric}`}>
              <Info className="h-4 w-4" />
            </button>
          )}
          <span className={cn("text-sm font-bold", isPos ? 'text-teal-600' : 'text-rose-500')}>{v.variance > 0 ? '+' : ''}{v.variance}%</span>
        </div>
      </div>
      {showInfo && info && (
        <div className="mx-3 mb-3 p-3 bg-indigo-50 border border-indigo-100 rounded-lg text-xs leading-relaxed space-y-1.5" data-testid={`variance-info-panel-${v.metric}`}>
          <p className="text-indigo-800"><strong>What this compares:</strong> {info.what}</p>
          <p className="text-indigo-700"><strong>Impact of this variance:</strong> {info.impact}</p>
          <p className="text-indigo-700"><strong>Improvement potential:</strong> {info.potential}</p>
          <p className="text-indigo-600"><strong>Recommended next steps:</strong> {info.next}</p>
        </div>
      )}
    </div>
  );
}

function VarianceContent({ data, focusMetric }) {
  const navigate = useNavigate();
  if (!data?.has_data || (!data.has_model && !data.has_actuals)) {
    return (
      <div className="text-center py-12">
        <GitCompare className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-slate-800 mb-2">Variance Analysis</h3>
        <p className="text-sm text-slate-500 max-w-md mx-auto">
          {!data?.has_model && "Run an ROE model first (ROE Modeller), then "}
          {!data?.has_actuals && "import actual data (Actuals tab) "}
          to compare projections against reality.
        </p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/roe-modeller')} data-testid="model-roe-btn-empty">
          <Target className="h-4 w-4 mr-2" /> Go to ROE Modeller
        </Button>
      </div>
    );
  }
  const chartData = (data.variance || []).map(v => ({ name: v.metric, Modelled: v.modelled, Actual: v.actual }));
  const pctFormatter = (val) => `${val}%`;
  return (
    <div className="space-y-6" data-testid="roe-variance-tab">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><GitCompare className="h-4 w-4 text-indigo-600" /> Modelled vs Actual
          <TileInfoButton
            testid="roe-modelled-actual-info"
            title="Modelled vs Actual"
            body="Side-by-side comparison of what the ROE Modeller PROJECTED for each metric (gray) against what your real L2/L3/Motivation imports actually MEASURED (teal). A small gap means the model is well-calibrated; a large gap is a learning signal — either the model needs tuning, or the program didn't land as planned. Use the ROE Modeller (top-right) to re-run scenarios after each variance review."
          />
        </CardTitle></CardHeader>
        <CardContent>
          <p className="text-xs text-slate-500 mb-3">Comparing ROE model projections against actual observed data.</p>
          {!data.has_actuals && <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 mb-4">No actual data imported yet.</div>}
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Tooltip formatter={pctFormatter} />
              <Legend />
              <Bar dataKey="Modelled" fill="#94a3b8" radius={[4,4,0,0]}>
                <LabelList dataKey="Modelled" position="top" formatter={pctFormatter} style={{ fontSize: 11, fontWeight: 600, fill: '#64748b' }} />
              </Bar>
              <Bar dataKey="Actual" fill="#14b8a6" radius={[4,4,0,0]}>
                <LabelList dataKey="Actual" position="top" formatter={pctFormatter} style={{ fontSize: 11, fontWeight: 600, fill: '#0d9488' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Variance Detail</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(data.variance || []).map(v => <VarianceDetailRow key={v.metric} v={v} focus={focusMetric === v.metric} />)}
        </CardContent>
      </Card>
    </div>
  );
}
